import type { Workspace, CsmScorecard } from "./types";

/* CSM scorecards — byte-compatible port of api/src/reporting/scorecards.ts so
 * the SPA computes the same team roll-up offline as the API serves live. */

const DAY = 86_400_000;

export function computeScorecards(ws: Workspace, today = "2026-08-17"): CsmScorecard[] {
  const now = +new Date(today);
  const npsSurveyIds = new Set(ws.surveys.filter((s) => s.type === "nps").map((s) => s.id));
  const owners = [...new Set(ws.accounts.map((a) => a.ownerId))];

  return owners
    .map((userId): CsmScorecard => {
      const u = ws.users.find((x) => x.id === userId);
      const accts = ws.accounts.filter((a) => a.ownerId === userId);
      const acctIds = new Set(accts.map((a) => a.id));

      const health = { good: 0, watch: 0, risk: 0 };
      for (const a of accts) health[a.healthBand]++;
      const bookArr = accts.reduce((n, a) => n + a.arr, 0);
      const atRiskArr = accts.filter((a) => a.healthBand === "risk").reduce((n, a) => n + a.arr, 0);
      const avgHealth = accts.length ? Math.round(accts.reduce((n, a) => n + a.health, 0) / accts.length) : 0;

      const myTasks = ws.tasks.filter((t) => t.ownerId === userId && t.status !== "done");
      const openTasks = myTasks.length;
      const overdueTasks = myTasks.filter((t) => +new Date(t.due) < now).length;

      const wins = ws.renewals.filter((r) => acctIds.has(r.accountId) && (+new Date(r.renewalDate) - now) >= 0 && (+new Date(r.renewalDate) - now) <= 90 * DAY);
      const renewalArr90 = wins.reduce((n, r) => n + r.arr, 0);
      const forecastArr90 = wins.reduce((n, r) => n + r.forecastAmount, 0);

      const npsRs = ws.surveyResponses.filter((r) => npsSurveyIds.has(r.surveyId) && acctIds.has(r.accountId));
      const prom = npsRs.filter((r) => r.score >= 9).length;
      const det = npsRs.filter((r) => r.score <= 6).length;
      const nps = npsRs.length ? Math.round(((prom - det) / npsRs.length) * 100) : null;

      return {
        userId, name: u?.name ?? userId, initials: u?.initials ?? "?", color: u?.color ?? "#9B9587", role: u?.role ?? "CSM",
        accounts: accts.length, bookArr, atRiskArr, health, avgHealth,
        openTasks, overdueTasks, renewals90: wins.length, renewalArr90, forecastArr90, nps, npsResponses: npsRs.length,
      };
    })
    .sort((a, b) => b.bookArr - a.bookArr);
}
